/**
 * Профиль дела в Битрикс24 (ТЗ п. 5.1).
 *
 * Профиль читается и пишется напрямую в поле сделки, минуя серверную
 * часть: её недоступность не должна приводить к потере введённого (ТЗ п. 8.2).
 * Всё остальное — генерация, справочники, шаблоны, резервные копии, журнал,
 * блокировка — делает сервер, см. `api.ts`.
 *
 * Коды полям сделки присваивает портал: `UF_CRM_1758312045`. Осмысленного кода
 * у них нет, поэтому поля находятся по НАЗВАНИЮ при открытии карточки
 * (`storage/diagnostics.ts`), а до тех пор действуют заготовки.
 */

import type { CaseProfile } from '@bd/domain';
import { computeTotals } from '@bd/domain';
import { call } from './rest.js';

const DEFAULT_CASE_FIELDS = {
  profile: 'ufCrmProfileJson',
  version: 'ufCrmProfileVersion',
  courtName: 'ufCrmCourtName',
  debtTotal: 'ufCrmDebtTotal',
  creditorsCount: 'ufCrmCreditorsCount',
  hasProperty: 'ufCrmHasProperty',
  packageStatus: 'ufCrmPackageStatus',
  lastGeneratedAt: 'ufCrmLastGeneratedAt',
  folderId: 'ufCrmFolderId',
} as const;

export type CaseFieldKey = keyof typeof DEFAULT_CASE_FIELDS;

/** Действующие имена полей. Уточняются один раз при запуске. */
export const CASE_FIELDS: Record<CaseFieldKey, string> = { ...DEFAULT_CASE_FIELDS };

export { DEFAULT_CASE_FIELDS };

/**
 * Идентификатор сущности приходит из параметров размещения вкладки,
 * а не из настроек: портал сообщает его сам, и лишнее место для опечатки
 * не нужно.
 */
let entityTypeId = 0;

export function useEntityType(id: number): void {
  if (id > 0) entityTypeId = id;
}

/** Уточнение имён полей — из раздела «Настройка» либо при запуске карточки. */
export function applyCaseFieldNames(names: Partial<Record<CaseFieldKey, string>>): void {
  for (const [key, name] of Object.entries(names)) {
    if (key in CASE_FIELDS && typeof name === 'string' && name.trim()) {
      CASE_FIELDS[key as CaseFieldKey] = name.trim();
    }
  }
}

export interface CaseItem {
  id: number;
  title?: string;
  [key: string]: unknown;
}

export async function loadCase(caseId: string): Promise<CaseItem | null> {
  const data = await call<{ item?: CaseItem }>('crm.item.get', { entityTypeId, id: caseId });
  return data?.item ?? null;
}

export function extractProfile(item: CaseItem | null): CaseProfile | null {
  const raw = item?.[CASE_FIELDS.profile];
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    return JSON.parse(raw) as CaseProfile;
  } catch {
    // Повреждённое поле не должно ронять карточку: юрист увидит пустую форму
    // и предупреждение, а прежняя редакция лежит в резервной копии на сервере
    return null;
  }
}

export async function saveProfile(caseId: string, profile: CaseProfile): Promise<void> {
  const totals = computeTotals(profile);
  const title = [profile.debtor.last, profile.debtor.first, profile.debtor.middle].filter(Boolean).join(' ');

  await call('crm.item.update', {
    entityTypeId,
    id: caseId,
    fields: {
      ...(title ? { title } : {}),
      [CASE_FIELDS.profile]: JSON.stringify(profile),
      [CASE_FIELDS.version]: profile.revision,
      // Поля-индикаторы для списка дел и отчётности (ТЗ п. 5.1)
      [CASE_FIELDS.courtName]: profile.court.name,
      [CASE_FIELDS.debtTotal]: totals.total / 100,
      [CASE_FIELDS.creditorsCount]: totals.creditorsCount,
      [CASE_FIELDS.hasProperty]: profile.property.hasProperty ? 'Y' : 'N',
      [CASE_FIELDS.packageStatus]: profile.stage,
    },
  });
}

/** Отметка о сформированном пакете — для списка дел (ТЗ п. 5.1). */
export async function markGenerated(caseId: string, folderId: string): Promise<void> {
  await call('crm.item.update', {
    entityTypeId,
    id: caseId,
    fields: {
      [CASE_FIELDS.lastGeneratedAt]: new Date().toISOString(),
      [CASE_FIELDS.folderId]: folderId,
    },
  });
}

/** Кто работает с делом — для блокировки и журнала. */
export async function currentUser(): Promise<{ id: string; name: string }> {
  const user = await call<{ ID: string; NAME?: string; LAST_NAME?: string }>('user.current');
  return {
    id: String(user?.ID ?? ''),
    name: [user?.NAME, user?.LAST_NAME].filter(Boolean).join(' ') || 'сотрудник',
  };
}

export { entityTypeId as caseEntityTypeId };
